import { useEffect, useState } from 'react';
import { getWeather } from '@/lib/api';
import { getIcon } from '@/lib/iconMap';

function formatTime(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function formatDate(date) {
  return date
    .toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    .toUpperCase();
}

// Subtle "digital HUD" widget — live clock (HH:MM:SS), date, and current DMV
// weather. Desktop-only accent (hidden on mobile to avoid crowding the
// MobileQuickBar), fixed to the opposite corner from the floating Book Now
// button to keep the two from ever overlapping.
export const DigitalHUD = () => {
  const [now, setNow] = useState(new Date());
  const [weather, setWeather] = useState(null);

  useEffect(() => {
    const tick = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    const fetchWeather = () => getWeather().then(setWeather).catch(() => setWeather(null));
    fetchWeather();
    const refresh = setInterval(fetchWeather, 10 * 60 * 1000);
    return () => clearInterval(refresh);
  }, []);

  const WeatherIcon = weather ? getIcon(weather.icon) : null;
  const degree = String.fromCharCode(176);

  return (
    <div
      className="hidden lg:flex fixed bottom-5 left-5 z-30 items-center gap-3 rounded-xl border border-[hsl(var(--lxra-border))] bg-[#0A0A0A]/70 backdrop-blur-md px-4 py-2.5 shadow-lg shadow-black/30"
      data-testid="digital-hud"
    >
      <div className="flex flex-col leading-none">
        <span className="lxra-mono text-sm text-[hsl(var(--lxra-gold))] tracking-wider" data-testid="digital-hud-clock">
          {formatTime(now)}
        </span>
        <span className="lxra-mono text-[10px] text-[hsl(var(--lxra-muted))] tracking-[0.1em] mt-0.5" data-testid="digital-hud-date">
          {formatDate(now)}
        </span>
      </div>

      {weather && (
        <>
          <span className="h-8 w-px bg-[hsl(var(--lxra-border))]" />
          <div className="flex items-center gap-1.5" data-testid="digital-hud-weather" title={weather.condition}>
            {WeatherIcon && <WeatherIcon className="h-4 w-4 text-[hsl(var(--lxra-gold))]" />}
            <span className="lxra-mono text-sm text-[hsl(var(--lxra-fg))]">
              {Math.round(weather.temperature_f)}{degree}F
            </span>
          </div>
        </>
      )}
    </div>
  );
};
